import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Group, Text, ThemeIcon } from "@mantine/core";
import { IconArrowRight, IconHistory } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { compareVersions } from "compare-versions";

import { useSelectedProductSummary } from "@hooks/useSelectedProduct";
import { releaseNotes } from "./releaseNotesData";
import { latestRelease } from "./releaseCatalog";

const LATEST_VERSION = latestRelease(releaseNotes, false)?.version;

function isUpdateAvailable(deviceVersion?: string | null): boolean {
  if (!deviceVersion || !LATEST_VERSION) return false;
  try {
    return compareVersions(LATEST_VERSION, deviceVersion) > 0;
  } catch {
    return false;
  }
}

const VersionControlCard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const selectedProduct = useSelectedProductSummary();
  const deviceName = selectedProduct?.alias || selectedProduct?.uuid || "";
  const updateAvailable = useMemo(
    () => isUpdateAvailable(selectedProduct?.version),
    [selectedProduct?.version],
  );

  const goToVersionControl = () => {
    if (selectedProduct) {
      navigate(`/version-control?pid=${selectedProduct.uuid}`);
    }
  };

  const title = updateAvailable
    ? t("releaseNotes.versionControlUpdateTitle")
    : t("DevicePageVersionControlLink");

  const subtext = !selectedProduct
    ? t("releaseNotes.versionControlNoDevice")
    : updateAvailable
      ? t("releaseNotes.versionControlUpdateSubtext", { device: deviceName })
      : t("releaseNotes.versionControlForDevice", { device: deviceName });

  return (
    <Card className="release-index-card" radius="xl" padding="lg">
      <Group justify="space-between" wrap="wrap" align="center" gap="md">
        <Group gap="sm" wrap="nowrap" align="center">
          <ThemeIcon
            variant="light"
            color={updateAvailable ? "orange" : "blue"}
            radius="xl"
            size={42}
          >
            <IconHistory size={22} />
          </ThemeIcon>
          <div>
            <Text className="release-stat__value">{title}</Text>
            <Text className="release-stat__label">{subtext}</Text>
          </div>
        </Group>
        <Button
          variant={updateAvailable ? "filled" : "light"}
          color={updateAvailable ? "orange" : undefined}
          rightSection={<IconArrowRight size={16} />}
          disabled={!selectedProduct}
          onClick={goToVersionControl}
        >
          {updateAvailable
            ? t("releaseNotes.versionControlUpdate")
            : t("DevicePageVersionControlLink")}
        </Button>
      </Group>
    </Card>
  );
};

export default VersionControlCard;
